import * as d3 from "d3";
import React from "react";
import { MouseEvent, useEffect, useMemo, useRef, useState } from "react";

export interface IGroupedData {
  label: string;
  values: number[];
}
export interface ILineData {
  index: number;
  value: number;
}
interface Props {
  data: IGroupedData[];
  CurrencySign: string;
  PercentageSign: string;
  legendsHOB: string[];
  colorsForBars: string[];
  average: number[];
  YoYGrowth: number[];
  Height: number;
  MarginLeft: number;
}

interface BarProps {
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  onMouseEnter: (e: MouseEvent<SVGPathElement>) => void;
  onMouseLeave: () => void;
}

interface Tooltip {
  x: number;
  y: number;
  index: number;
}

function Bar({
  x,
  y,
  width,
  height,
  color,
  onMouseEnter,
  onMouseLeave,
}: BarProps) {
  const radius = height === 0 ? 0 : 3.0;
  return (
    <path
      key={`Rotate--rect-group-path` + x}
      d={`
      m${x},${y + radius}
      a${radius},${radius} 0 0 1 ${radius},${-radius}
      h${width - 2 * radius}
      a${radius},${radius} 0 0 1 ${radius},${radius}
      v${height - radius}
      h-${width}
      z
    `}
      fill={color}
      onMouseEnter={(event) => onMouseEnter(event)}
      onMouseLeave={onMouseLeave}
    />
  );
}
export function BarChartWithXValuesRotate({
  data,
  PercentageSign,
  CurrencySign,
  legendsHOB,
  colorsForBars,
  average,
  YoYGrowth,
  Height,
  MarginLeft,
}: Props) {
  //  useEffect(() => {}, []);
  const [tooltip, setTooltip] = useState<Tooltip | null>(null);
  const axisBottomRef = useRef<SVGGElement>(null);
  const axisLeftRef = useRef<SVGGElement>(null);

  const chartRef = useRef(null);
  const svgContainer: any = d3.select(chartRef.current).node();
  var BottomDivWidth = svgContainer?.getBoundingClientRect()?.width || 700;

  const margin = { top: 15, right: 0, bottom: 210, left: MarginLeft };
  let width = BottomDivWidth - 15 - margin.left - margin.right || 0;
  const height = Height - margin.top - margin.bottom - 140;

  const labels = data.map(({ label }) => label);
  var sublabels: any = [];
  if (data[0]) sublabels = Object.keys(data[0].values);
  const scaleX = d3.scaleBand().domain(labels).range([0, width]).padding(0.2);
  var maxYscale = 0;
  const [refresh, setrefresh] = useState(false);
  data.map((d) => {
    var localMax = 0;
    d.values.map((v) => {
      localMax = v;

      if (localMax > maxYscale) maxYscale = localMax;
    });
  });
  maxYscale = maxYscale + 10;
  const scaleY = d3.scaleLinear().domain([0, maxYscale]).range([height, 0]);

  const subscaleX = d3
    .scaleBand()
    .domain(sublabels)
    .range([0, scaleX.bandwidth()])
    .padding(0.1);

  useEffect(() => {
    const svgContainer: any = d3.select(chartRef.current).node();
    var BottomDivWidth = svgContainer?.getBoundingClientRect()?.width || 700;

    width = BottomDivWidth - 15 - margin.left - margin.right || 0;

    setrefresh(!refresh);
  }, [width]);

  useEffect(() => {
    if (axisBottomRef.current) {
      d3.select(axisBottomRef.current)
        .call(d3.axisBottom(scaleX))
        .selectAll("text")
        .style("text-anchor", "end")
        .attr("dx", "-.8em")
        .attr("dy", ".15em")
        .attr("transform", "rotate(-65)")
        .text(function (d: any) {
          return d;
          //   return d.length > 14 ? d.substring(0, 13) + "..." : d;
        });
    }

    if (axisLeftRef.current) {
      d3.select(axisLeftRef.current).call(
        d3
          .axisLeft(scaleY)
          .tickValues(d3.range(0, maxYscale, Math.round(maxYscale / 5)))
          .tickFormat(function (d, i) {
            return CurrencySign + d?.toLocaleString() + PercentageSign;
          })
      );
    }
  }, [scaleX, scaleY]);

  return (
    <div key={`Rotate--rect-group-DivOuter`} className="" ref={chartRef}>
      <svg
        key={`Rotate--rect-group-SV`}
        width={width + margin.left + margin.right}
        height={height + margin.top + margin.bottom}
      >
        <g
          key={`Rotate--rect-group-1`}
          transform={`translate(${margin.left}, ${margin.top})`}
        >
          <g
            key={`Rotate--rect-group-2`}
            ref={axisBottomRef}
            transform={`translate(0, ${height})`}
          />
          <g ref={axisLeftRef} key={`Rotate--rect-group-3`} />
          {data.map(({ label, values }, groupIndex) => (
            <g
              key={`Rotate----rect-group-${groupIndex + label}`}
              transform={`translate(${scaleX(label)}, 0)`}
            >
              {values.map((value, barIndex) => (
                <>
                  <Bar
                    key={`dfffrect-${barIndex + label}`}
                    x={subscaleX(String(barIndex)) || 0}
                    y={scaleY(value)}
                    width={subscaleX.bandwidth()}
                    height={height - scaleY(value)}
                    color={colorsForBars[barIndex]}
                    onMouseEnter={(event) => {
                      setTooltip({
                        x: event.clientX,
                        y: event.clientY,
                        index: groupIndex,
                      });
                    }}
                    onMouseLeave={() => setTooltip(null)}
                  />
                </>
              ))}
            </g>
          ))}
        </g>
      </svg>
      {
        <div className="Legend-main3">
          {legendsHOB &&
            legendsHOB.length > 0 &&
            legendsHOB.map((Item: any, index: any) => (
              <div key={`divLegens-${index}`} className="leg-item">
                <span className={"clegend-" + index}></span>
                <p>{Item}</p>
              </div>
            ))}
        </div>
      }
      {tooltip !== null ? (
        <div
          key={`Rotate--rect-group-div`}
          className="tooltip"
          style={{ top: tooltip.y, left: tooltip.x }}
        >
          <span className="tooltip__title">{labels[tooltip.index]}</span>
          <table className="tooltip__table">
            <tbody>
              {" "}
              {legendsHOB &&
                legendsHOB.length > 0 &&
                legendsHOB.map((Item: any, index: any) => (
                  <>
                    <tr key={"rowModel" + index}>
                      <td>{Item}</td>
                      <td>
                        {CurrencySign}
                        {data[tooltip.index].values[index]?.toLocaleString()}
                        {PercentageSign}
                      </td>
                    </tr>
                  </>
                ))}
              {average.length > 0 && (
                <tr>
                  <td>Average</td>
                  <td>
                    {CurrencySign}
                    {average[tooltip.index].toLocaleString()}
                    {PercentageSign}
                  </td>
                </tr>
              )}
              {YoYGrowth.length > 0 && (
                <tr>
                  <td>YoYGrowth</td>
                  <td>
                    {YoYGrowth[tooltip.index].toLocaleString()}
                    {"%"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
